import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faCalendarDay,
  faCalendarHeart,
  faChevronDown,
  faCompass,
  faHouseTree,
  faMagnifyingGlassLocation,
  faMapLocationDot,
  faRoute,
  faSuitcaseRolling,
  faTowerBroadcast,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTripContext } from '@/contexts/TripContext';
import { MenuPopover, useMenuPopoverClose } from '@/design-system/primitives/MenuPopover';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import { Switch } from '@/design-system/primitives/Switch';
import { twx } from '@/utils/tailwind';
import { TripSelectorContent } from './TripSelectorContent';

export const tripModes = ['explore', 'scout', 'curate', 'travel'] as const;
type TripMode = (typeof tripModes)[number];

const modeLabel: Record<TripMode, string> = {
  explore: 'Exploring',
  scout: 'Scouting',
  curate: 'Curating',
  travel: 'Traveling',
};

export const modeButtonLabel: Record<TripMode, string> = {
  explore: 'Explore',
  scout: 'Scout',
  curate: 'Curate',
  travel: 'Travel',
};

export const modeIcon: Record<TripMode, IconDefinition> = {
  explore: faCompass,
  scout: faMagnifyingGlassLocation,
  curate: faCalendarHeart,
  travel: faHouseTree,
};

export const modeBlurb: Record<TripMode, string> = {
  explore: 'Explore new destinations',
  scout: 'Find the cutest local places',
  curate: 'Plan the perfect itinerary',
  travel: 'Get lost in the woods',
};

type FAIconStyle = React.CSSProperties & { [key: `--fa-${string}`]: string | undefined };

export interface TripModeMenuProps {
  className?: string;
  mode: TripMode;
  onModeChange: (mode: TripMode) => void;
  showRoutes: boolean;
  onShowRoutesChange: (show: boolean) => void;
  /** "Only this trip's places on the map" — hides search-result pins so only
   * the user's scouted/curated places show. Toggle lives here next to
   * show-routes for visual cohesion. */
  tripPlacesOnly: boolean;
  onTripPlacesOnlyChange: (only: boolean) => void;
  /** Travel-mode-only sub-toggle that further filters trip places to
   *  just the active day's scheduled places (plus all unscheduled —
   *  logistics like lodging always show every day). Only meaningful
   *  when `tripPlacesOnly` is also on. */
  tripPlacesTodayOnly: boolean;
  onTripPlacesTodayOnlyChange: (only: boolean) => void;
  /** Opens the iOS "Add to Home Screen" instructions modal. Threaded
   *  through to TripSelectorContent (the install row lives there). The
   *  modal itself is rendered at the App level — outside the popover. */
  onShowInstallInstructions?: () => void;
}

/** Mode-picker tile inside the popover — separate component so it can call
 * `useMenuPopoverClose()` to dismiss the popover after picking a mode. */
const ModeTile = ({
  m,
  isActive,
  disabled,
  onSelect,
}: {
  m: TripMode;
  isActive: boolean;
  disabled: boolean;
  onSelect: (m: TripMode) => void;
}) => {
  const close = useMenuPopoverClose();
  return (
    <button
      type="button"
      onClick={() => {
        onSelect(m);
        close();
      }}
      disabled={disabled}
      title={disabled ? 'Select a trip above to enable' : undefined}
      className={twx(
        'flex items-center gap-2 px-2.5 py-2 rounded-xl border text-left transition-colors',
        isActive
          ? 'bg-primary-subtle border-primary-main text-primary-focal'
          : 'bg-white border-content-border text-content-text hover:border-primary-main hover:bg-primary-subtle/40',
        disabled ? 'opacity-40 cursor-not-allowed hover:bg-white hover:border-content-border' : '',
      )}
    >
      <FontAwesomeIcon
        icon={modeIcon[m]}
        style={
          {
            width: 16,
            height: 16,
            '--fa-primary-color': isActive
              ? 'var(--color-primary-emphasized)'
              : 'var(--color-content-subtle)',
            // Inactive secondary uses content-disabled (lighter than
            // content-deemphasized) so the inactive icon doesn't read
            // as a dense purple blob. Plus a lower opacity on both
            // states for stronger primary-vs-secondary contrast.
            '--fa-secondary-color': isActive
              ? 'var(--color-primary-main)'
              : 'var(--color-content-disabled)',
            '--fa-secondary-opacity': '0.55',
          } as FAIconStyle
        }
      />
      <span className="flex flex-col leading-tight min-w-0">
        <span className="text-xs font-semibold">{modeButtonLabel[m]}</span>
        <span className="text-[10px] text-content-subtle truncate">{modeBlurb[m]}</span>
      </span>
    </button>
  );
};

/** Wraps `TripSelectorContent` so its `onAction` callback can close the
 * popover via the context-provided hook. */
const TripSelectorInsideMenu = ({
  onShowInstallInstructions,
}: {
  onShowInstallInstructions?: () => void;
}) => {
  const close = useMenuPopoverClose();
  return (
    <TripSelectorContent onAction={close} onShowInstallInstructions={onShowInstallInstructions} />
  );
};

export const TripModeMenu = ({
  className,
  mode,
  onModeChange,
  showRoutes,
  onShowRoutesChange,
  tripPlacesOnly,
  onTripPlacesOnlyChange,
  tripPlacesTodayOnly,
  onTripPlacesTodayOnlyChange,
  onShowInstallInstructions,
}: TripModeMenuProps) => {
  const { trips, selectedTripId, deselectTrip } = useTripContext();
  const selectedTrip = trips.find((t) => t.id === selectedTripId) ?? null;
  const hasTrip = Boolean(selectedTripId);

  const handleModeSelect = (m: TripMode) => {
    if (m === 'explore' && hasTrip) {
      // Explore implies no active trip — deselect when entering it.
      deselectTrip();
    }
    onModeChange(m);
  };

  const tripSubtitle = selectedTrip ? selectedTrip.name : 'Just exploring';
  // Routes toggle is only meaningful where a map is shown with a trip — that's
  // Scout. Curate has no map; Travel always shows routes by definition.
  const showRoutesRow = mode === 'scout' && hasTrip;
  // "Only trip places" toggle is meaningful any time the user is working on a
  // trip outside Explore. Today it has a visible effect only in Scout (the
  // sole mode that mixes search results with trip places), but exposing it
  // here keeps the trip-scoped settings in one place.
  const showTripPlacesOnlyRow = mode !== 'explore' && hasTrip;
  // "Only today's places" — Travel-mode-only sub-toggle disclosed
  // beneath "Only this trip's places". Hidden in other modes; hidden
  // when the parent toggle is off (the sub-filter only applies on top
  // of the parent's filter).
  const showTodayOnlyRow = mode === 'travel' && hasTrip && tripPlacesOnly;
  const showOptionsDivider = showRoutesRow || showTripPlacesOnlyRow;

  return (
    <MenuPopover
      className={twx('shrink-0', className)}
      align="right"
      width="lg"
      renderTrigger={({ ref, onClick, isOpen }) => (
        <button
          ref={ref}
          type="button"
          onClick={onClick}
          aria-label={`${modeLabel[mode]} — ${tripSubtitle}`}
          // Always uses the same white-pill styling as the SearchBar so both
          // surfaces look like sibling controls inside the right header chip.
          // No border — the outer right chip provides the visual frame.
          className={twx(
            'flex items-center gap-2 h-10 px-3 rounded-lg bg-white transition-all shrink-0 relative',
            selectedTrip ? 'text-primary-focal' : 'text-content-text',
          )}
        >
          <FontAwesomeIcon
            icon={faMapLocationDot}
            style={
              {
                width: 22,
                height: 22,
                '--fa-primary-color': 'var(--color-primary-emphasized)',
                '--fa-secondary-color': 'var(--color-primary-main)',
                // Lower secondary opacity so the duotone has more
                // tonal contrast — primary-emphasized + primary-main are
                // close in luminance, so muting secondary lets primary
                // do the heavy lifting visually.
                '--fa-secondary-opacity': '0.5',
              } as FAIconStyle
            }
          />
          <div className="flex flex-col items-start leading-tight">
            <span
              className={twx(
                'text-[10px] font-semibold uppercase tracking-wider opacity-80',
                selectedTrip ? 'text-primary-focal' : 'text-content-subtle',
              )}
            >
              {modeLabel[mode]}
            </span>
            <span className="text-sm font-medium max-w-[140px] truncate hidden sm:block">
              {tripSubtitle}
            </span>
          </div>
          {/* Icon-only live indicator — appears only when the selected trip is
              actually shared with someone. Soft pulse to telegraph "real-time". */}
          {(selectedTrip?.collaborators?.length ?? 0) > 0 && (
            <span
              role="img"
              className="relative inline-flex items-center justify-center w-5 h-5 rounded-full bg-quaternary-subtle text-quaternary-emphasized"
              aria-label="Collaborating live on this trip"
              title="Collaborating live"
            >
              <span
                aria-hidden
                className="absolute inset-1 rounded-full bg-quaternary-main/40 motion-safe:animate-ping pointer-events-none"
              />
              <FontAwesomeIcon
                icon={faTowerBroadcast}
                style={{ width: 10, height: 10 }}
                className="relative"
              />
            </span>
          )}
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{ width: 9, height: 9 }}
            className={twx('transition-transform duration-150', isOpen ? 'rotate-180' : '')}
          />
        </button>
      )}
    >
      {/* 1. Trip selector — picking a trip unlocks the modes below. */}
      <TripSelectorInsideMenu onShowInstallInstructions={onShowInstallInstructions} />

      <div className="h-px bg-content-divider" />

      {/* 2. Mode picker. Travel/Curate/Scout require a trip; Explore doesn't. */}
      <div className="p-3">
        <SectionEyebrow className="px-1 pb-2">Mode</SectionEyebrow>
        <div className="grid grid-cols-2 gap-1.5">
          {tripModes.map((m) => {
            const requiresTrip = m !== 'explore';
            const disabled = requiresTrip && !hasTrip;
            const isActive = mode === m;
            return (
              <ModeTile
                key={m}
                m={m}
                isActive={isActive}
                disabled={disabled}
                onSelect={handleModeSelect}
              />
            );
          })}
        </div>
        {!hasTrip && (
          <p className="px-1 pt-2 text-xs text-content-subtle leading-snug">
            Select a trip above to <strong>Scout</strong>, <strong>Curate</strong>, and{' '}
            <strong>Travel</strong>.
          </p>
        )}
      </div>

      {/* 3. Contextual options — toggles that depend on the current
          trip-mode context. Each row is independently gated; the divider
          and outer container only show when at least one row applies. */}
      {showOptionsDivider && (
        <>
          <div className="h-px bg-content-divider" />
          <div className="p-1.5 space-y-0.5">
            {showRoutesRow && (
              <Switch icon={faRoute} checked={showRoutes} onChange={onShowRoutesChange}>
                Show routes on map
              </Switch>
            )}
            {showTripPlacesOnlyRow && (
              <Switch
                icon={faSuitcaseRolling}
                checked={tripPlacesOnly}
                onChange={onTripPlacesOnlyChange}
              >
                Only this trip's places
              </Switch>
            )}
            {showTodayOnlyRow && (
              // Nested ml-4 + smaller hierarchy reads as "this controls
              // the parent toggle's behavior further".
              <Switch
                icon={faCalendarDay}
                checked={tripPlacesTodayOnly}
                onChange={onTripPlacesTodayOnlyChange}
                className="ml-4"
              >
                Only today's places
              </Switch>
            )}
          </div>
        </>
      )}
    </MenuPopover>
  );
};
